import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { EventDTO, RunDTO, RunEventDTO } from "@roomate/core";
import { getToken } from "./api.ts";
import { isGuardEvent } from "./lib/guard.ts";

type Presence = { actorId: string; activity: string | null; taskId: string | null; at: number };
type LiveState = {
  connected: boolean;
  recent: EventDTO[];
  presence: Record<string, Presence>;
  /** Latest state of runs seen live, by run id. */
  runs: Record<string, RunDTO>;
  /** Transcript lines that arrived live, by run id (bounded). */
  runEvents: Record<string, RunEventDTO[]>;
};

const LiveContext = createContext<LiveState>({ connected: false, recent: [], presence: {}, runs: {}, runEvents: {} });

export const useLive = () => useContext(LiveContext);

/**
 * Query roots refetched when the socket opens. Events missed while it was down never arrive as
 * messages, so anything a screen reads has to be in this list. `workers` and `map` are the
 * Workers card and the codebase map panel.
 */
export const RECONNECT_ROOTS = ["overview", "intent", "nodes", "project", "task", "decisions", "events", "bridge", "mission", "threads", "messages", "status", "workers", "map", "projects", "routines", "lessons", "corrections", "onboarding", "chief", "homebox"] as const;

/**
 * Query keys one live event should refetch. Workers and the codebase map are per project: a task
 * or worker event refreshes that project's card, not every project's. Reconnect still uses
 * `RECONNECT_ROOTS`, because events missed while the socket was down never arrive here.
 */
export function liveQueryKeysForEvent(event: Pick<EventDTO, "type" | "projectId" | "subjectType">): string[][] {
  const keys: string[][] = [["overview"], ["events"], ["metrics"]];
  const add = (...roots: string[]) => {
    for (const root of roots) keys.push([root]);
  };
  const taskTouched = event.subjectType === "task" || event.type.startsWith("task.");
  if (taskTouched || event.type.startsWith("evidence") || event.type.startsWith("verification")) add("project", "task", "bridge", "mission", "status", "projects"); // "projects": the Projects page's stage counts
  if (taskTouched) keys.push(["intent", "ideas"]); // the idea status steps follow their tasks; not the whole intent root
  if (event.type.startsWith("main_check.") || event.type === "project.main_moved") add("status");
  if (event.type === "project.check_command_changed") add("projects", "status");
  if (event.type.startsWith("decision.") || event.type.startsWith("intent.")) add("decisions", "task", "project", "intent", "bridge", "mission", "status", "projects");
  // "onboarding": the first-run card follows the team and the machines (docs/specs/starter-team.md).
  if (event.type.startsWith("actor.") || event.type === "office.starter_team_created") add("actors", "onboarding");
  if (event.type.startsWith("node.")) add("nodes", "bridge", "onboarding");
  if (event.type.startsWith("run.")) add("runs", "run", "task", "costs", "bridge", "mission", "projects", "routines");
  if (event.type.startsWith("schedule.")) add("mission", "routines");
  if (event.type.startsWith("routine.")) add("routines");
  if (event.type.startsWith("message.")) add("threads", "messages", "task");
  if (event.type.startsWith("rule.")) add("rules");
  if (event.type.startsWith("chief.")) add("chief", "task", "bridge");
  if (event.type === "homebox.updated") add("homebox"); // a Home chat answer arrived (#1552)
  if (event.type.startsWith("lesson.")) add("lessons", "corrections");
  if (event.type.startsWith("correction.")) add("corrections", "lessons");
  if (event.type === "project.created" || event.type === "project.repo_state") add("projects");
  // Archiving hides a project from the lists, switcher, Inbox and Bridge, and flips the banner and gating on its pages.
  if (event.type === "project.archived" || event.type === "project.unarchived") add("projects", "bridge", "project", "decisions");
  if (event.type === "area.penalized") add("project", "task");
  // filed7d is counted from tasks, so a cancel or a new worker: label has to refresh the card too.
  if (event.projectId && (event.type.startsWith("worker.") || taskTouched)) keys.push(["workers", event.projectId]);
  if (event.projectId && event.type.startsWith("worker.")) keys.push(["map", event.projectId]);
  if (isGuardEvent(event.type)) add("guard");
  return keys;
}

/** Keeps a WebSocket open and turns server events into query invalidations, so every screen stays live. */
export function LiveProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [state, setState] = useState<LiveState>({ connected: false, recent: [], presence: {}, runs: {}, runEvents: {} });
  const pending = useRef(new Set<string>());
  const timer = useRef<number | null>(null);

  useEffect(() => {
    let socket: WebSocket | null = null;
    let retry = 0;
    let stopped = false;

    const flush = () => {
      timer.current = null;
      const batched = [...pending.current];
      pending.current.clear();
      for (const raw of batched) void qc.invalidateQueries({ queryKey: JSON.parse(raw) as string[] });
    };
    const queue = (key: readonly string[]) => {
      pending.current.add(JSON.stringify(key));
      // Coalesce bursts (one evidence submission emits several events).
      if (timer.current === null) timer.current = window.setTimeout(flush, 120);
    };
    const invalidate = (...roots: string[]) => {
      for (const r of roots) queue([r]);
    };

    const connect = () => {
      const token = getToken();
      if (!token || stopped) return;
      const proto = location.protocol === "https:" ? "wss" : "ws";
      socket = new WebSocket(`${proto}://${location.host}/ws?token=${encodeURIComponent(token)}`);
      socket.onopen = () => {
        retry = 0;
        setState((s) => ({ ...s, connected: true }));
        invalidate(...RECONNECT_ROOTS);
      };
      socket.onclose = () => {
        setState((s) => ({ ...s, connected: false }));
        if (!stopped) window.setTimeout(connect, Math.min(10_000, 500 * 2 ** retry++));
      };
      socket.onmessage = (msg) => {
        const data = JSON.parse(String(msg.data));
        if (data.kind === "presence") {
          setState((s) => ({ ...s, presence: { ...s.presence, [data.actorId]: data } }));
          invalidate("overview");
          return;
        }
        if (data.kind === "run") {
          const run = data.run as RunDTO;
          setState((s) => ({ ...s, runs: { ...s.runs, [run.id]: run } }));
          invalidate("runs", "run", "task", "overview", "bridge", "mission");
          return;
        }
        if (data.kind === "run_event") {
          const ev = data.event as RunEventDTO;
          setState((s) => {
            const list = [...(s.runEvents[ev.runId] ?? []), ev].slice(-3000);
            return { ...s, runEvents: { ...s.runEvents, [ev.runId]: list } };
          });
          return;
        }
        if (data.kind !== "event") return;
        const event = data.event as EventDTO;
        setState((s) => ({ ...s, recent: [event, ...s.recent].slice(0, 50) }));
        for (const key of liveQueryKeysForEvent(event)) queue(key);
      };
    };
    connect();
    return () => {
      stopped = true;
      socket?.close();
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [qc]);

  return <LiveContext.Provider value={state}>{children}</LiveContext.Provider>;
}
